// Length-prefixed inline component codec — frontend port of dash-api's
// `services/ai-assistant/src/streaming/inline-component.ts` (MINSKY-16478 / MINSKY-16479).
//
// Wire format: `[[type:len:payload]]` where:
//   - `type` is one of a small, closed allowlist (`INLINE_COMPONENT_TYPES` below).
//   - `len` is the UTF-8 BYTE length (not JS string `.length`, which counts UTF-16 code units) of
//     `payload`, written as ASCII decimal digits.
//   - `payload` is exactly `JSON.stringify(someObject)` for that component's data.
//   - The trailing `]]` is a literal sanity-check terminator, not a scan target: the decoder
//     already knows `len` from the header, so it reads exactly `len` bytes for the payload and
//     then expects (and consumes) the two-character `]]` immediately after — it never scans
//     forward looking for a closing bracket pair. This is the whole point of length-prefixing over
//     a naive delimiter-scanning parser: a payload whose own JSON content contains literal
//     `[[`/`]]` substrings (e.g. a document excerpt quoting "the [[REDACTED]] program") can never
//     confuse this decoder, because it is never looking for those characters to decide where the
//     payload ends — only the length prefix decides that. Do NOT "simplify" this into a
//     delimiter-scanning parser; that reintroduces exactly the bug this format exists to avoid.
//
// Chart-AI entity shorthand (also accepted): `[[entity:REF:Label]]` where REF is a non-decimal
// entity id (Q… / D…) and Label is the human name. Chart answer synthesis prompts the model to
// emit this form; when the length-prefixed header parse fails because the middle segment is not
// digits, the scanner tries this shorthand before recovering as literal text.
//
// Unknown/malformed `type` handling mirrors the backend codec exactly: a `type` outside the
// closed allowlist is passed through as literal text once the marker is otherwise structurally
// complete (an older client talking to a newer server that added a new component type should
// still render the surrounding prose untouched, not blow up or drop the whole message). A
// *structurally malformed* header (e.g. a non-numeric length) or a bad/missing terminator is
// different — those are real decode errors (reported via `onError`), not "not implemented yet",
// and the scanner recovers from them by treating the offending `[[` as two literal characters and
// resuming immediately after, so one bad marker can never wedge the whole stream.

/** Closed, small set of supported inline component types — see this module's header comment. */
export const INLINE_COMPONENT_TYPES = ['document', 'entity', 'citation', 'chart', 'kb', 'user'];

export function isKnownInlineComponentType(type) {
    return INLINE_COMPONENT_TYPES.includes(type);
}

/** Strip LLM-invented `[[entity::Label|TYPE]] · Property]` wire text — KB sources are not shown. */
export function repairMalformedEntityDoubleColonKb(text) {
    if (text == null || text === '') {
        return text;
    }
    return String(text).replace(
        /\[+\[entity::([^|\]]+?)(?:\|[^\]]+)?\]\]+\s*·\s*([^\]]+)\]/gi,
        ''
    );
}


export class InlineComponentDecodeError extends Error {
    constructor(message, rawMarker) {
        super(message);
        this.name = 'InlineComponentDecodeError';
        this.rawMarker = rawMarker;
    }
}

/** UTF-8 byte length of `str` — the browser/Node-agnostic equivalent of `Buffer.byteLength`. */
function byteLengthOf(str) {
    return new TextEncoder().encode(str).length;
}

/**
 * Encodes `payload` as a `[[type:len:payload]]` inline component marker. `len` is the UTF-8
 * **byte** length of `JSON.stringify(payload)`, not its JS string `.length` — these differ for
 * any non-ASCII character, so using `.length` here would silently produce a wrong byte count and
 * desync every decoder that reads `len` bytes.
 */
export function encodeInlineComponent(type, payload) {
    const json = JSON.stringify(payload);
    const byteLength = byteLengthOf(json);
    return `[[${type}:${byteLength}:${json}]]`;
}

/** Same header grammar the streaming scanner uses, but requires the whole header to already be
 * present (used by the non-streaming `decodeInlineComponent` below, which operates on one
 * already-complete marker). Returns `undefined` if `text` doesn't have a complete, well-formed
 * header at `start`. */
function parseHeader(text, start) {
    if (!text.startsWith('[[', start)) return undefined;
    const firstColon = text.indexOf(':', start + 2);
    if (firstColon === -1) return undefined;
    const type = text.slice(start + 2, firstColon);
    if (type.length === 0) return undefined;
    const secondColon = text.indexOf(':', firstColon + 1);
    if (secondColon === -1) return undefined;
    const lenText = text.slice(firstColon + 1, secondColon);
    if (lenText.length === 0 || !/^\d+$/.test(lenText)) return undefined;
    const len = Number(lenText);
    if (!Number.isSafeInteger(len)) return undefined;
    return {type, len, payloadStart: secondColon + 1};
}

/**
 * Chart-AI answer shorthand: `[[entity:REF:Label]]` (the model is instructed to cite entities this
 * way). Distinct from the length-prefixed `[[entity:len:{json}]]` codec: the middle segment is a
 * KB/Wikidata/D ref, not a decimal byte length.
 *
 * Incomplete closed forms `[[entity:Q123]]` / `[[entity:Q123:]]` (no label) must be stripped —
 * never shown as raw wire text or Q-ids.
 *
 * @returns {{status: 'ok', type: 'entity', payload: object, end: number}
 *   | {status: 'strip', end: number}
 *   | {status: 'incomplete'}
 *   | {status: 'no'}}
 */
export function tryParseEntityShorthandMarker(text, start = 0) {
    const prefix = '[[entity:';
    if (!text.startsWith(prefix, start)) {
        return {status: 'no'};
    }
    const refStart = start + prefix.length;
    const secondColon = text.indexOf(':', refStart);
    if (secondColon === -1) {
        // One-colon form: either still streaming, or incomplete `[[entity:Q123]]` (strip when closed).
        const close = text.indexOf(']]', refStart);
        if (close === -1) {
            return {status: 'incomplete'};
        }
        const ref = text.slice(refStart, close).trim();
        if (ref && looksLikeEntityRef(ref.split('|')[0].trim())) {
            return {status: 'strip', end: close + 2};
        }
        return {status: 'no'};
    }
    const ref = text.slice(refStart, secondColon).trim();
    // Length-prefixed headers use a pure-decimal middle segment; never treat those as shorthand.
    if (!ref || /^\d+$/.test(ref)) {
        return {status: 'no'};
    }
    const close = text.indexOf(']]', secondColon + 1);
    if (close === -1) {
        return {status: 'incomplete'};
    }
    let labelPart = text.slice(secondColon + 1, close).trim();
    if (!labelPart) {
        // `[[entity:Q123:]]` — incomplete / empty label: strip from display.
        if (looksLikeEntityRef(ref)) {
            return {status: 'strip', end: close + 2};
        }
        return {status: 'no'};
    }
    let entityType = null;
    const pipeIdx = labelPart.lastIndexOf('|');
    if (pipeIdx > 0) {
        const maybeType = labelPart.slice(pipeIdx + 1).trim();
        if (/^[A-Z][A-Z0-9_]{1,9}$/.test(maybeType)) {
            entityType = maybeType;
            labelPart = labelPart.slice(0, pipeIdx).trim();
        }
    }
    // Wire form is sometimes `entities.canonical_name|entity_summaries.canonicalName`.
    // Display the entities-row name only.
    const label = canonicalEntityDisplayName(labelPart);
    if (!label) {
        if (looksLikeEntityRef(ref)) {
            return {status: 'strip', end: close + 2};
        }
        return {status: 'no'};
    }
    // Label that is itself a bare Q/D id is not displayable — strip the marker.
    if (!isDisplayableEntityLabel(label, ref)) {
        return {status: 'strip', end: close + 2};
    }
    const payload = {
        entityRef: ref,
        wikidataId: ref,
        id: ref,
        label,
    };
    if (entityType) {
        payload.entityType = entityType;
    }
    return {
        status: 'ok',
        type: 'entity',
        payload,
        end: close + 2,
    };
}

/**
 * Visible entity name. LLM/wire labels are sometimes
 * `entities.canonical_name|entity_summaries.canonicalName` — keep the first segment.
 */
export function canonicalEntityDisplayName(label) {
    if (label == null) {
        return null;
    }
    const trimmed = String(label).trim();
    if (!trimmed) {
        return null;
    }
    const pipeIdx = trimmed.indexOf('|');
    if (pipeIdx <= 0) {
        return trimmed;
    }
    const first = trimmed.slice(0, pipeIdx).trim();
    return first || trimmed;
}

/** Human-readable label for an entity chip payload, or null when only a raw ref is available. */
export function entityDisplayLabel(payload) {
    if (!payload || typeof payload !== 'object') {
        return null;
    }
    const label = payload.title || payload.name || payload.label;
    if (label == null || String(label).trim() === '') {
        return null;
    }
    return canonicalEntityDisplayName(String(label));
}

/** True when a value is a bare KB entity ref (Q123, D55577). */
export function looksLikeEntityRef(value) {
    if (value == null) {
        return false;
    }
    const trimmed = String(value).trim();
    return /^Q\d+$/i.test(trimmed) || /^D[1-9]\d*$/i.test(trimmed);
}

/** True when an entity has a human-readable label (not a bare Q-id / D-id). */
export function isDisplayableEntityLabel(label, entityRef = null) {
    if (label == null || String(label).trim() === '') {
        return false;
    }
    const normalized = String(label).trim();
    if (looksLikeEntityRef(normalized)) {
        return false;
    }
    if (entityRef != null
        && normalized.toUpperCase() === String(entityRef).trim().toUpperCase()
        && looksLikeEntityRef(entityRef)) {
        return false;
    }
    return true;
}

/** True when a decoded entity chip payload is safe to show in UI or add prompts. */
export function isDisplayableEntityPayload(payload) {
    const entityRef = resolveEntityRefFromPayload(payload);
    const label = entityDisplayLabel(payload);
    return Boolean(label && isDisplayableEntityLabel(label, entityRef));
}

/** Human-readable label for a user chip payload, or null when unavailable. */
export function userDisplayLabel(payload) {
    if (!payload || typeof payload !== 'object') {
        return null;
    }
    const label = payload.displayName || payload.userId;
    if (label == null || String(label).trim() === '') {
        return null;
    }
    return String(label).trim();
}

function escapeRegExp(str) {
    return str.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
}

/** Trailing bracketed KB/Wikidata refs such as `[Q123]` or `[Q123 unknown]`. */
const TRAILING_TECHNICAL_ENTITY_REF = /\s*\[(?:Q\d+|D[1-9]\d*)(?:\s+\w+)?\]$/i;

/** Same bracketed refs anywhere in prose — stripped so answers read naturally. */
const INLINE_TECHNICAL_ENTITY_REF = /\s*\[(?:Q\d+|D[1-9]\d*)(?:\s+\w+)?\]/gi;

/** Parenthesized entity refs such as `(Q123)`. */
const INLINE_TECHNICAL_ENTITY_PARENS = /\s*\((?:Q\d+|D[1-9]\d*)\)/gi;

/**
 * Bare Q/D tokens in prose (≥2 digits after Q so titles like "Q3 Report" survive).
 * Never strip inside valid `[[...]]` markers — callers must run this on text segments only,
 * or use {@link stripTechnicalEntityRefs} which also drops leftover incomplete markers.
 */
const BARE_ENTITY_REF_IN_PROSE = /(?<![A-Za-z0-9_])(?:Q\d{2,}|D[1-9]\d*)(?![A-Za-z0-9_])/gi;

/** Leftover incomplete chip markers that escaped the decoder. */
const INCOMPLETE_ENTITY_MARKER = /\[\[entity:(?:Q\d+|D[1-9]\d*)(?::)?]]/gi;

/** LLM-invented entity:: KB source wire text that survived other cleanup. */
const ENTITY_DOUBLE_COLON_KB = /\[+\[entity::[^|\]]+(?:\|[^\]]+)?\]\]+\s*·\s*[^\]]+\]/gi;

/** Bracket KB citations such as `[Entity · KB summary]` — not shown as source links. */
const KB_BRACKET_CITATION = /\[[^\]]+\s·\s[^\]]+\]/gi;

function stripTrailingTechnicalEntityRef(text) {
    return text.replace(TRAILING_TECHNICAL_ENTITY_REF, '');
}

function isUnlabeledEntityLine(line) {
    const trimmed = String(line || '').trim();
    if (!trimmed) {
        return false;
    }
    const withoutMarker = trimmed.replace(/^[-*>\s]+/, '').trim();
    if (looksLikeEntityRef(withoutMarker)) {
        return true;
    }
    if (/^(?:an entity referenced as|entity referenced as|referenced entity)\s+(?:Q\d+|D[1-9]\d*)/i.test(withoutMarker)) {
        return true;
    }
    if (/\(no label(?: provided)?\)/i.test(withoutMarker) && /(?:Q\d+|D[1-9]\d*)/i.test(withoutMarker)) {
        return true;
    }
    return false;
}

/** Removes prose about entities that only have a bare Q-id / D-id and no human label. */
export function stripUnlabeledEntityProse(text) {
    const inlineCleaned = String(text || '')
        .replace(/\b(?:An entity referenced as|entity referenced as)\s+(?:Q\d+|D[1-9]\d*)\s*(?:\([^)]*no label[^)]*\)|\(no label provided\))/gi, '')
        .replace(/\b(?:Q\d+|D[1-9]\d*)\s*\(no label provided\)/gi, '');

    return inlineCleaned
        .split('\n')
        .filter((line) => !isUnlabeledEntityLine(line))
        .filter((line) => !/^[\s>*\-]+\s*$/.test(line))
        .join('\n')
        .replace(/\n{3,}/g, '\n\n')
        .replace(/^\s+$/gm, '');
}

/** Removes bracketed/parenthesized KB refs, bare Q/D tokens, and incomplete entity markers. */
export function stripTechnicalEntityRefs(text) {
    return stripUnlabeledEntityProse(String(text || '')
        .replace(/<!--[\s\S]*?--?>/g, '')
        .replace(/<!--[\s\S]*$/g, '')
        .replace(ENTITY_DOUBLE_COLON_KB, '')
        .replace(KB_BRACKET_CITATION, '')
        .replace(INCOMPLETE_ENTITY_MARKER, '')
        .replace(INLINE_TECHNICAL_ENTITY_REF, '')
        .replace(INLINE_TECHNICAL_ENTITY_PARENS, '')
        .replace(BARE_ENTITY_REF_IN_PROSE, '')
        .replace(/[ \t]{2,}/g, ' ')
        .replace(/ +([,.;:!?])/g, '$1'));
}

function stripTrailingDisplayLabel(text, label) {
    if (!label) {
        return text;
    }
    const escaped = escapeRegExp(label);
    const withWhitespace = new RegExp(`(\\s+)${escaped}\\s*$`, 'i');
    const wsMatch = text.match(withWhitespace);
    if (wsMatch) {
        return `${text.slice(0, text.length - wsMatch[0].length)} `;
    }
    const withPunctuation = new RegExp(`([,;])\\s*${escaped}\\s*$`, 'i');
    if (withPunctuation.test(text)) {
        return text.replace(withPunctuation, '$1 ');
    }
    const tight = new RegExp(`${escaped}\\s*$`, 'i');
    if (tight.test(text)) {
        return text.replace(tight, '');
    }
    return text;
}

function appendTextSegment(segments, value) {
    if (!value) {
        return;
    }
    const last = segments[segments.length - 1];
    if (last?.type === 'text') {
        last.value += value;
    } else {
        segments.push({type: 'text', value});
    }
}

function polishLinkedComponentSegment(segments, label) {
    const prev = segments[segments.length - 1];
    if (prev?.type !== 'text') {
        return;
    }
    prev.value = stripTrailingTechnicalEntityRef(prev.value);
    if (label) {
        prev.value = stripTrailingDisplayLabel(prev.value, label);
    }
}

/**
 * Cleans decoded inline segments for display: when an entity/user link follows prose that already
 * names the same person or org, the duplicate plain-text label (and any trailing `[Q…]` refs) are
 * removed so only the interactive chip/link remains.
 *
 * @param {Array<{type: 'text', value: string} | {type: string, payload: unknown}>} segments
 */
export function polishInlineSegmentsForDisplay(segments) {
    const result = [];

    for (const segment of segments) {
        if (segment.type === 'entity') {
            if (!isDisplayableEntityPayload(segment.payload)) {
                continue;
            }
            const label = entityDisplayLabel(segment.payload);
            polishLinkedComponentSegment(result, label);
            if (label) {
                result.push(segment);
            }
            continue;
        }

        if (segment.type === 'user') {
            const label = userDisplayLabel(segment.payload);
            polishLinkedComponentSegment(result, label);
            if (label) {
                result.push(segment);
            }
            continue;
        }

        if (segment.type === 'text') {
            appendTextSegment(result, stripTechnicalEntityRefs(segment.value));
            continue;
        }

        if (segment.type === 'kb') {
            continue;
        }

        result.push(segment);
    }

    return result;
}

/** Entity id from a decoded entity chip payload (length-prefixed JSON or shorthand). */
export function resolveEntityRefFromPayload(payload) {
    if (!payload || typeof payload !== 'object') {
        return null;
    }
    const raw = payload.entityRef || payload.wikidataId || payload.id;
    if (raw == null) {
        return null;
    }
    const ref = String(raw).trim();
    return ref || null;
}

/**
 * Extract unique entity markers from a markdown snippet (paragraph, list item, etc.).
 * @param {string} text
 * @returns {Array<{entityRef: string, label: string, entityType: string|null, payload: object}>}
 */
export function extractEntitiesFromMarkdownText(text) {
    if (!text || !text.includes('[[')) {
        return [];
    }
    const {segments} = decodeInlineComponentsToSegments(text);
    const seen = new Set();
    const entities = [];
    for (const segment of segments) {
        if (segment.type !== 'entity') {
            continue;
        }
        const entityRef = resolveEntityRefFromPayload(segment.payload);
        if (!entityRef || !isDisplayableEntityPayload(segment.payload)) {
            continue;
        }
        const key = entityRef.toUpperCase();
        if (seen.has(key)) {
            continue;
        }
        seen.add(key);
        entities.push({
            entityRef,
            label: entityDisplayLabel(segment.payload) || entityRef,
            entityType: segment.payload?.entityType || null,
            payload: segment.payload,
        });
    }
    return entities;
}

/**
 * Non-streaming convenience decoder: decodes a single, already-complete `[[type:len:payload]]`
 * marker (no surrounding prose). Throws `InlineComponentDecodeError` for a structurally malformed
 * marker — this function does NOT apply the streaming scanner's "unknown type passes through as
 * text" leniency, since there is no surrounding text for it to pass through into; use
 * `createInlineComponentStreamDecoder` for that behavior over a real text stream.
 */
export function decodeInlineComponent(marker) {
    const header = parseHeader(marker, 0);
    if (!header) {
        throw new InlineComponentDecodeError('Not a well-formed [[type:len:payload]] marker', marker);
    }
    const {type, len, payloadStart} = header;

    const encoder = new TextEncoder();
    const decoder = new TextDecoder('utf-8');
    const fullBytes = encoder.encode(marker);
    const payloadStartByte = byteLengthOf(marker.slice(0, payloadStart));
    const payloadEndByte = payloadStartByte + len;
    const terminatorEndByte = payloadEndByte + 2;
    if (terminatorEndByte > fullBytes.byteLength) {
        throw new InlineComponentDecodeError('Marker is truncated before its declared length is satisfied', marker);
    }

    const payloadJson = decoder.decode(fullBytes.subarray(payloadStartByte, payloadEndByte));
    const terminator = decoder.decode(fullBytes.subarray(payloadEndByte, terminatorEndByte));
    if (terminator !== ']]') {
        throw new InlineComponentDecodeError('Marker is missing its "]]" terminator at the declared length', marker);
    }

    let payload;
    try {
        payload = JSON.parse(payloadJson);
    } catch (err) {
        throw new InlineComponentDecodeError(`Payload is not valid JSON: ${err instanceof Error ? err.message : String(err)}`, marker);
    }
    return {type, payload};
}

// --- Streaming-safe decoder --------------------------------------------------------------------
//
// `decodeInlineComponent` above requires one complete, already-fully-buffered marker. A real SSE
// loop delivers text in arbitrary-sized chunks, and a `[[type:len:payload]]` marker's boundaries
// can fall anywhere relative to a chunk boundary — mid-header, mid-length-digits, mid-payload
// (including inside a multi-byte UTF-8 character), mid-terminator. `createInlineComponentStreamDecoder`
// is an incremental scanner: callers `push()` one chunk at a time and get back the text that is
// now safe to render immediately (`safeText` — never includes a still-buffered partial marker)
// plus any components that fully resolved during that push.

/** Same header grammar as `parseHeader`, but distinguishes "not enough data yet" (`incomplete`,
 * keep buffering) from "already have enough to know this is broken" (`invalid`, recover now). */
function tryParseHeader(text, start) {
    const firstColon = text.indexOf(':', start + 2);
    if (firstColon === -1) return {status: 'incomplete'};
    const type = text.slice(start + 2, firstColon);
    if (type.length === 0) return {status: 'invalid'};
    const secondColon = text.indexOf(':', firstColon + 1);
    if (secondColon === -1) return {status: 'incomplete'};
    const lenText = text.slice(firstColon + 1, secondColon);
    if (lenText.length === 0 || !/^\d+$/.test(lenText)) return {status: 'invalid'};
    const len = Number(lenText);
    if (!Number.isSafeInteger(len)) return {status: 'invalid'};
    return {status: 'ok', type, len, payloadStart: secondColon + 1};
}

/** Finds the character index within `text` (starting from `startCharIndex`) at which the
 * cumulative UTF-8 byte length of the characters consumed so far first reaches `targetByteLength`.
 * Returns `undefined` if `text` runs out before reaching it (caller should wait for more data).
 * Walks by Unicode code point (not UTF-16 code unit) so surrogate pairs are never split. */
function charIndexForByteLength(text, startCharIndex, targetByteLength) {
    if (targetByteLength === 0) return startCharIndex;
    let bytes = 0;
    let i = startCharIndex;
    while (i < text.length) {
        const codePoint = text.codePointAt(i);
        const charStr = String.fromCodePoint(codePoint);
        bytes += byteLengthOf(charStr);
        i += charStr.length;
        if (bytes === targetByteLength) return i;
    }
    return undefined;
}

/**
 * Creates an incremental `[[type:len:payload]]` marker decoder.
 *
 * @param {{onError?: (err: InlineComponentDecodeError) => void}} [options]
 * @returns {{
 *   push: (chunk: string) => {safeText: string, components: Array<{type: string, payload: unknown}>},
 *   finalize: () => {safeText: string},
 * }}
 */
export function createInlineComponentStreamDecoder(options = {}) {
    let buffer = '';

    function push(chunk) {
        buffer += chunk;
        let safeText = '';
        const components = [];

        // eslint-disable-next-line no-constant-condition
        while (true) {
            const idx = buffer.indexOf('[[');

            if (idx === -1) {
                // No marker start anywhere in the buffer. A lone trailing '[' might be the first
                // half of a '[[' split exactly at this chunk boundary — hold it back.
                if (buffer.endsWith('[')) {
                    safeText += buffer.slice(0, -1);
                    buffer = '[';
                } else {
                    safeText += buffer;
                    buffer = '';
                }
                break;
            }

            // Emit everything before the marker start as safe text, then try to resolve the
            // marker itself out of `remaining` (which always starts with '[[').
            safeText += buffer.slice(0, idx);
            const remaining = buffer.slice(idx);

            const header = tryParseHeader(remaining, 0);

            if (header.status === 'incomplete') {
                // Closed incomplete chips like [[entity:Q123]] never get a second ':' — try
                // shorthand/strip before buffering forever or leaking raw wire text.
                const close = remaining.indexOf(']]');
                if (close !== -1) {
                    const shorthand = tryParseEntityShorthandMarker(remaining, 0);
                    if (shorthand.status === 'ok') {
                        components.push({type: shorthand.type, payload: shorthand.payload});
                        buffer = remaining.slice(shorthand.end);
                        continue;
                    }
                    if (shorthand.status === 'strip') {
                        buffer = remaining.slice(shorthand.end);
                        continue;
                    }
                }
                buffer = remaining; // wait for more data before we can even read the header
                break;
            }

            if (header.status === 'invalid') {
                const shorthand = tryParseEntityShorthandMarker(remaining, 0);
                if (shorthand.status === 'incomplete') {
                    buffer = remaining;
                    break;
                }
                if (shorthand.status === 'ok') {
                    components.push({type: shorthand.type, payload: shorthand.payload});
                    buffer = remaining.slice(shorthand.end);
                    continue;
                }
                if (shorthand.status === 'strip') {
                    buffer = remaining.slice(shorthand.end);
                    continue;
                }
                options.onError?.(new InlineComponentDecodeError(
                    'Not a well-formed [[type:len:... header', remaining.slice(0, Math.min(remaining.length, 64))
                ));
                safeText += '[['; // recover: treat the offending delimiter as literal text
                buffer = remaining.slice(2);
                continue;
            }

            const {type, len, payloadStart} = header;
            const availableAfterPayloadStart = byteLengthOf(remaining.slice(payloadStart));
            if (availableAfterPayloadStart < len + 2) {
                buffer = remaining; // header is known, but payload+terminator hasn't fully arrived yet
                break;
            }

            const payloadEnd = charIndexForByteLength(remaining, payloadStart, len); // safe: byte count already confirmed available
            const payloadJson = remaining.slice(payloadStart, payloadEnd);
            const terminator = remaining.slice(payloadEnd, payloadEnd + 2);

            if (terminator !== ']]') {
                options.onError?.(new InlineComponentDecodeError(
                    'Marker is missing its "]]" terminator at the declared length', remaining.slice(0, payloadEnd + 2)
                ));
                safeText += '[[';
                buffer = remaining.slice(2);
                continue;
            }

            const wholeMarker = remaining.slice(0, payloadEnd + 2);

            if (!isKnownInlineComponentType(type)) {
                // Unrecognized type, but structurally complete — pass the whole marker through
                // unchanged (see this section's header comment).
                safeText += wholeMarker;
                buffer = remaining.slice(payloadEnd + 2);
                continue;
            }

            let payload;
            try {
                payload = JSON.parse(payloadJson);
            } catch (err) {
                options.onError?.(new InlineComponentDecodeError(
                    `Payload is not valid JSON: ${err instanceof Error ? err.message : String(err)}`,
                    wholeMarker
                ));
                safeText += '[[';
                buffer = remaining.slice(2);
                continue;
            }

            components.push({type, payload});
            buffer = remaining.slice(payloadEnd + 2);
        }

        return {safeText, components};
    }

    function finalize() {
        const leftover = buffer;
        buffer = '';
        if (leftover.startsWith('[[')) {
            options.onError?.(new InlineComponentDecodeError(
                'Stream ended with an incomplete [[type:len:payload]] marker', leftover.slice(0, Math.min(leftover.length, 64))
            ));
        }
        return {safeText: leftover};
    }

    return {push, finalize};
}

/**
 * Convenience wrapper for UI rendering: decodes a single, complete text string (e.g. a
 * `run-completed` event's whole `finalAnswer`, which — unlike a token-streamed chat response —
 * arrives in one shot, not chunked) into an ORDERED array of segments so a renderer can splice
 * inline component chips back in at their exact original position. `push`/`finalize` above
 * intentionally do not expose this ordering (their contract mirrors the backend decoder's
 * `{safeText, components}` shape exactly, for adversarial-test parity); this wrapper reuses the
 * exact same header-parsing rules (`tryParseHeader`/`charIndexForByteLength`) over the whole,
 * already-complete text — never a second decoding algorithm, just a differently-shaped result.
 *
 * @returns {{segments: Array<{type: 'text', value: string} | {type: string, payload: unknown}>}}
 */
export function decodeInlineComponentsToSegments(text, options = {}) {
    const segments = [];
    let buffer = repairMalformedEntityDoubleColonKb(text);

    function pushText(value) {
        if (!value) return;
        const last = segments[segments.length - 1];
        if (last?.type === 'text') {
            last.value += value;
        } else {
            segments.push({type: 'text', value});
        }
    }

    // eslint-disable-next-line no-constant-condition
    while (true) {
        const idx = buffer.indexOf('[[');

        if (idx === -1) {
            pushText(buffer);
            buffer = '';
            break;
        }

        pushText(buffer.slice(0, idx));
        const remaining = buffer.slice(idx);
        const header = tryParseHeader(remaining, 0);

        // There is no "more data coming" here (the whole text is already in hand), so both
        // "incomplete" (never enough bytes to even read the header) and a payload that never
        // reaches its declared length are treated the same way `finalize()` treats a
        // permanently-truncated tail: reported via `onError`, kept as literal trailing text.
        // Exception: closed incomplete entity chips [[entity:Q123]] — strip / decode via shorthand.
        if (header.status === 'incomplete') {
            const close = remaining.indexOf(']]');
            if (close !== -1) {
                const shorthand = tryParseEntityShorthandMarker(remaining, 0);
                if (shorthand.status === 'ok') {
                    segments.push({type: shorthand.type, payload: shorthand.payload});
                    buffer = remaining.slice(shorthand.end);
                    continue;
                }
                if (shorthand.status === 'strip') {
                    buffer = remaining.slice(shorthand.end);
                    continue;
                }
            }
            options.onError?.(new InlineComponentDecodeError(
                'Text ended with an incomplete [[type:len:payload]] marker', remaining.slice(0, Math.min(remaining.length, 64))
            ));
            pushText(remaining);
            buffer = '';
            break;
        }

        if (header.status === 'invalid') {
            const shorthand = tryParseEntityShorthandMarker(remaining, 0);
            if (shorthand.status === 'incomplete') {
                options.onError?.(new InlineComponentDecodeError(
                    'Text ended with an incomplete [[entity:REF:Label]] marker', remaining.slice(0, Math.min(remaining.length, 64))
                ));
                pushText(remaining);
                buffer = '';
                break;
            }
            if (shorthand.status === 'ok') {
                segments.push({type: shorthand.type, payload: shorthand.payload});
                buffer = remaining.slice(shorthand.end);
                continue;
            }
            if (shorthand.status === 'strip') {
                buffer = remaining.slice(shorthand.end);
                continue;
            }
            options.onError?.(new InlineComponentDecodeError(
                'Not a well-formed [[type:len:... header', remaining.slice(0, Math.min(remaining.length, 64))
            ));
            pushText('[[');
            buffer = remaining.slice(2);
            continue;
        }

        const {type, len, payloadStart} = header;
        const availableAfterPayloadStart = byteLengthOf(remaining.slice(payloadStart));
        if (availableAfterPayloadStart < len + 2) {
            options.onError?.(new InlineComponentDecodeError(
                'Text ended with an incomplete [[type:len:payload]] marker', remaining.slice(0, Math.min(remaining.length, 64))
            ));
            pushText(remaining);
            buffer = '';
            break;
        }

        const payloadEnd = charIndexForByteLength(remaining, payloadStart, len);
        const payloadJson = remaining.slice(payloadStart, payloadEnd);
        const terminator = remaining.slice(payloadEnd, payloadEnd + 2);

        if (terminator !== ']]') {
            options.onError?.(new InlineComponentDecodeError(
                'Marker is missing its "]]" terminator at the declared length', remaining.slice(0, payloadEnd + 2)
            ));
            pushText('[[');
            buffer = remaining.slice(2);
            continue;
        }

        const wholeMarker = remaining.slice(0, payloadEnd + 2);

        if (!isKnownInlineComponentType(type)) {
            pushText(wholeMarker);
            buffer = remaining.slice(payloadEnd + 2);
            continue;
        }

        let payload;
        try {
            payload = JSON.parse(payloadJson);
        } catch (err) {
            options.onError?.(new InlineComponentDecodeError(
                `Payload is not valid JSON: ${err instanceof Error ? err.message : String(err)}`,
                wholeMarker
            ));
            pushText('[[');
            buffer = remaining.slice(2);
            continue;
        }

        segments.push({type, payload});
        buffer = remaining.slice(payloadEnd + 2);
    }

    return {segments};
}
